import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { ActionsButton } from "@/components/ActionsButton";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Printer, Trash2, Send, FileText, Wallet, CheckCircle2, Clock, Download, Calendar, Layers, Plus, X, Boxes, Mail, ScanLine, Loader2,
} from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro, fmtDate, toInputDate, FATTURA_STATUS, formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { todayIso } from "@/lib/cantieri";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import QuotePDF from "@/components/QuotePDF";
import SendDialog from "@/components/SendDialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { downloadElementPdf } from "@/lib/pdf";

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-5 h-5" /></div>
    <div className="mt-3 font-heading text-2xl font-extrabold tracking-tight">{value}</div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </div>
);

const RATE_PRESETS = [
  { key: "unica", label: "Unica (100%)", rows: [{ etichetta: "Pagamento unico", percentuale: 100 }] },
  { key: "5050", label: "50% + 50%", rows: [{ etichetta: "Acconto", percentuale: 50 }, { etichetta: "Saldo", percentuale: 50 }] },
  { key: "304030", label: "30% + 40% + 30%", rows: [{ etichetta: "Anticipo", percentuale: 30 }, { etichetta: "Avanzamento", percentuale: 40 }, { etichetta: "Saldo", percentuale: 30 }] },
];

const incassatoOf = (i) => (i.rate && i.rate.length)
  ? i.rate.filter((r) => r.pagata).reduce((s, r) => s + (r.importo || 0), 0)
  : (i.stato === "pagata" ? i.totale : 0);

const materialiOf = (i) => (i.materiali || []).reduce((s, m) => s + (Number(m.importo) || 0), 0);

export default function Fatture() {
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [pdfDoc, setPdfDoc] = useState(null);
  const [sendDoc, setSendDoc] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [dueDoc, setDueDoc] = useState(null);
  const [dueVal, setDueVal] = useState("");
  const [rateDoc, setRateDoc] = useState(null);
  const [rateRows, setRateRows] = useState([]);
  const [savingRate, setSavingRate] = useState(false);
  const [matDoc, setMatDoc] = useState(null);
  const [matRows, setMatRows] = useState([]);
  const [matOcrLoading, setMatOcrLoading] = useState(false);
  const matReceiptRef = useRef(null);
  const [savingMat, setSavingMat] = useState(false);
  const [marginView, setMarginView] = useState("cantiere");
  const [params, setParams] = useSearchParams();
  const inviaId = params.get("invia");
  useEffect(() => {
    if (!inviaId || !invoices.length) return;
    const inv = invoices.find((x) => x.id === inviaId);
    if (inv) setSendDoc(inv);
  }, [inviaId, invoices]);
  const closeSend = () => {
    setSendDoc(null);
    if (inviaId) { const p = new URLSearchParams(params); p.delete("invia"); p.delete("canale"); setParams(p); }
  };

  const load = useCallback(() => {
    Promise.all([cachedGet("/invoices"), cachedGet("/contacts"), cachedGet("/settings")])
      .then(([i, c, s]) => { setInvoices(i.data); setContacts(c.data); setSettings(s.data); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (inv, stato) => {
    await api.patch(`/invoices/${inv.id}/status?stato=${stato}`);
    toast.success(`Fattura ${FATTURA_STATUS[stato].label.toLowerCase()}`);
    load();
  };
  const doDelete = async () => {
    await api.delete(`/invoices/${toDelete.id}`);
    toast.success("Fattura eliminata");
    setToDelete(null); load();
  };

  const sendReminder = async (x) => {
    try {
      const r = await api.post(`/invoices/${x.id}/send-reminder`);
      toast.success(`Promemoria inviato a ${r.data.to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openDue = (x) => { setDueDoc(x); setDueVal(toInputDate(x.scadenza_pagamento) || ""); };
  const saveDue = async () => {
    if (!dueVal) { toast.error("Seleziona una data di scadenza"); return; }
    await api.patch(`/invoices/${dueDoc.id}/scadenza?value=${encodeURIComponent(dueVal + "T12:00:00Z")}`);
    toast.success("Scadenza pagamento aggiornata");
    setDueDoc(null); load();
  };

  const openRate = (x) => {
    setRateDoc(x);
    if (x.rate && x.rate.length) {
      setRateRows(x.rate.map((r) => ({ id: r.id, etichetta: r.etichetta || "", percentuale: r.percentuale, scadenza: toInputDate(r.scadenza) || "", pagata: !!r.pagata })));
    } else {
      setRateRows([{ etichetta: "Pagamento unico", percentuale: 100, scadenza: "", pagata: x.stato === "pagata" }]);
    }
  };
  const applyPreset = (p) => setRateRows(p.rows.map((r) => ({ ...r, scadenza: "", pagata: false })));
  const updateRow = (idx, patch) => setRateRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => setRateRows((rows) => [...rows, { etichetta: `Rata ${rows.length + 1}`, percentuale: 0, scadenza: "", pagata: false }]);
  const removeRow = (idx) => setRateRows((rows) => rows.filter((_, i) => i !== idx));
  const rateSum = rateRows.reduce((s, r) => s + (Number(r.percentuale) || 0), 0);

  const saveRate = async () => {
    if (Math.round(rateSum) !== 100) { toast.error(`La somma delle percentuali deve essere 100% (ora ${rateSum}%)`); return; }
    setSavingRate(true);
    try {
      await api.put(`/invoices/${rateDoc.id}/rate`, {
        rate: rateRows.map((r) => ({
          id: r.id,
          etichetta: r.etichetta,
          percentuale: Number(r.percentuale) || 0,
          scadenza: r.scadenza ? r.scadenza + "T12:00:00Z" : "",
          pagata: !!r.pagata,
        })),
      });
      toast.success("Piano di pagamento salvato");
      setRateDoc(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingRate(false); }
  };

  const openMat = (x) => {
    setMatDoc(x);
    setMatRows((x.materiali || []).map((m) => ({ id: m.id, descrizione: m.descrizione || "", fornitore: m.fornitore || "", importo: m.importo || 0, data: toInputDate(m.data) || "" })));
  };
  const addMatRow = () => setMatRows((rows) => [...rows, { descrizione: "", fornitore: "", importo: 0, data: todayIso() }]);
  const updateMatRow = (idx, patch) => setMatRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeMatRow = (idx) => setMatRows((rows) => rows.filter((_, i) => i !== idx));
  const uploadMatReceipt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMatOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/ocr/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      setMatRows((rows) => [...rows, { descrizione: d.descrizione || "", fornitore: d.fonte || "", importo: d.importo || 0, data: d.data || todayIso() }]);
      toast.success(d.method === "ai" ? "Documento letto con l'IA — controlla i dati" : "Documento letto (OCR gratuito) — controlla i dati");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setMatOcrLoading(false); if (matReceiptRef.current) matReceiptRef.current.value = ""; }
  };
  const matRowsSum = matRows.reduce((s, r) => s + (Number(r.importo) || 0), 0);
  const saveMat = async () => {
    setSavingMat(true);
    try {
      await api.put(`/invoices/${matDoc.id}/materiali`, {
        materiali: matRows.filter((r) => (r.descrizione || "").trim()).map((r) => ({
          id: r.id, descrizione: r.descrizione, fornitore: r.fornitore,
          importo: Number(r.importo) || 0, data: r.data ? r.data + "T12:00:00Z" : "",
        })),
      });
      toast.success("Materiali e costi salvati");
      setMatDoc(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingMat(false); }
  };

  const dueBadge = (x) => {
    if (!x.scadenza_pagamento) return null;
    const overdue = x.stato !== "pagata" && new Date(x.scadenza_pagamento) < new Date();
    return <span className={`text-xs ${overdue ? "text-rose-600 dark:text-rose-400 font-semibold" : "text-muted-foreground"}`}>{fmtDate(x.scadenza_pagamento)}</span>;
  };

  const contactOf = (d) => contacts.find((c) => c.id === d?.contact_id);
  const filtered = invoices.filter((x) => !q || x.cliente_nome?.toLowerCase().includes(q.toLowerCase()) || x.numero?.includes(q));

  const paid = invoices.reduce((s, i) => s + incassatoOf(i), 0);
  const unpaid = invoices.reduce((s, i) => s + (i.totale - incassatoOf(i)), 0);
  const matTotal = invoices.reduce((s, i) => s + materialiOf(i), 0);
  const cantieri = useMemo(() => {
    const map = {};
    invoices.forEach((i) => {
      const key = i.contact_id || i.cliente_nome || "—";
      if (!map[key]) map[key] = { nome: i.cliente_nome || "—", numeri: [], fatturato: 0, incassato: 0, costi: 0 };
      map[key].numeri.push(i.numero);
      map[key].fatturato += i.totale || 0;
      map[key].incassato += incassatoOf(i);
      map[key].costi += materialiOf(i);
    });
    return Object.values(map).map((c) => ({ ...c, utile: c.incassato - c.costi }));
  }, [invoices]);

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={FileText} label="Fatture emesse" value={invoices.length} tone="bg-primary/10 text-primary" />
        <Stat icon={CheckCircle2} label="Incassato" value={euro(paid)} tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" />
        <Stat icon={Clock} label="Da incassare" value={euro(unpaid)} tone="bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" />
        <Stat icon={Boxes} label="Costi materiali" value={euro(matTotal)} tone="bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400" />
      </div>

      <div className="relative w-full sm:w-80">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Cerca cliente o N. fattura…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="invoices-search-input" />
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <Table data-testid="invoices-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>N.</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead className="hidden md:table-cell">Data</TableHead>
              <TableHead className="hidden md:table-cell">Rif. Prev.</TableHead>
              <TableHead className="hidden lg:table-cell">Scadenza pag.</TableHead>
              <TableHead className="text-right">Totale</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((x) => {
              const hasRate = x.rate && x.rate.length;
              const paidCount = hasRate ? x.rate.filter((r) => r.pagata).length : 0;
              return (
              <TableRow key={x.id} className="cursor-pointer" onClick={() => setPdfDoc(x)} data-testid={`invoice-row-${x.numero}`}>
                <TableCell className="font-mono text-xs font-medium">{x.numero}</TableCell>
                <TableCell>
                  <div className="font-medium">{x.cliente_nome}</div>
                  {x.oggetto && <div className="text-xs text-muted-foreground truncate max-w-[220px]">{x.oggetto}</div>}
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{fmtDate(x.data_emissione)}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{x.quote_numero || "—"}</TableCell>
                <TableCell className="hidden lg:table-cell">{dueBadge(x) || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-right font-semibold">{euro(x.totale)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={FATTURA_STATUS[x.stato]?.cls}>{FATTURA_STATUS[x.stato]?.label}</Badge>
                  {hasRate && (
                    <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`invoice-rate-summary-${x.numero}`}>
                      {paidCount}/{x.rate.length} rate · {euro(incassatoOf(x))}
                    </div>
                  )}
                  {materialiOf(x) > 0 && (
                    <div className="text-[10px] text-rose-600 dark:text-rose-400 mt-0.5" data-testid={`invoice-mat-summary-${x.numero}`}>
                      Costi materiali: {euro(materialiOf(x))}
                    </div>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <ActionsButton data-testid={`invoice-actions-${x.numero}`} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setPdfDoc(x)}><FileText className="w-4 h-4 mr-2" /> Anteprima / PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openRate(x)} data-testid={`invoice-rate-${x.numero}`}><Layers className="w-4 h-4 mr-2" /> Piano di pagamento (rate)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openMat(x)} data-testid={`invoice-materiali-${x.numero}`}><Boxes className="w-4 h-4 mr-2" /> Materiali e costi</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSendDoc(x)} data-testid={`invoice-send-${x.numero}`}><Send className="w-4 h-4 mr-2" /> Invia (WhatsApp/Email)</DropdownMenuItem>
                      {settings?.email_reminders_enabled && <DropdownMenuItem onClick={() => sendReminder(x)} data-testid={`invoice-remind-${x.numero}`}><Mail className="w-4 h-4 mr-2" /> Invia promemoria email</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => openDue(x)} data-testid={`invoice-due-${x.numero}`}><Calendar className="w-4 h-4 mr-2" /> Imposta scadenza pagamento</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {x.stato !== "pagata"
                        ? <DropdownMenuItem onClick={() => changeStatus(x, "pagata")}><Wallet className="w-4 h-4 mr-2" /> Segna come Pagata</DropdownMenuItem>
                        : <DropdownMenuItem onClick={() => changeStatus(x, "non_pagata")}><Clock className="w-4 h-4 mr-2" /> Segna Non pagata</DropdownMenuItem>}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => setToDelete(x)}><Trash2 className="w-4 h-4 mr-2" /> Elimina</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ); })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Nessuna fattura. Convertine una dal menu di un preventivo.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="margini-section">
        <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div className="font-heading font-semibold flex items-center gap-2"><Boxes className="w-4 h-4 text-primary" /> Marginalità</div>
          <Tabs value={marginView} onValueChange={setMarginView}>
            <TabsList data-testid="margini-view-tabs">
              <TabsTrigger value="cantiere" data-testid="margini-tab-cantiere">Per cantiere</TabsTrigger>
              <TabsTrigger value="fattura" data-testid="margini-tab-fattura">Per fattura</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{marginView === "cantiere" ? "Cantiere / Cliente" : "Fattura"}</TableHead>
              <TableHead className="text-right hidden sm:table-cell">Fatturato</TableHead>
              <TableHead className="text-right">Incassato</TableHead>
              <TableHead className="text-right">Costi materiali</TableHead>
              <TableHead className="text-right">Utile netto</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {marginView === "cantiere"
              ? cantieri.map((c, idx) => (
                  <TableRow key={idx} data-testid={`cantiere-row-${idx}`}>
                    <TableCell>
                      <div className="font-medium">{c.nome}</div>
                      <div className="text-xs text-muted-foreground">{c.numeri.length} fattur{c.numeri.length === 1 ? "a" : "e"} · N. {c.numeri.join(", ")}</div>
                    </TableCell>
                    <TableCell className="text-right hidden sm:table-cell text-muted-foreground">{euro(c.fatturato)}</TableCell>
                    <TableCell className="text-right">{euro(c.incassato)}</TableCell>
                    <TableCell className="text-right text-rose-600 dark:text-rose-400">{euro(c.costi)}</TableCell>
                    <TableCell className={`text-right font-semibold ${c.utile >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid={`cantiere-utile-${idx}`}>{euro(c.utile)}</TableCell>
                  </TableRow>
                ))
              : invoices.map((i) => {
                  const inc = incassatoOf(i); const co = materialiOf(i); const ut = inc - co;
                  return (
                    <TableRow key={i.id} data-testid={`margine-row-${i.numero}`}>
                      <TableCell>
                        <div className="font-medium">{i.cliente_nome}</div>
                        {i.oggetto && <div className="text-xs text-muted-foreground truncate max-w-[240px]">{i.oggetto}</div>}
                      </TableCell>
                      <TableCell className="text-right hidden sm:table-cell text-muted-foreground">{euro(i.totale)}</TableCell>
                      <TableCell className="text-right">{euro(inc)}</TableCell>
                      <TableCell className="text-right text-rose-600 dark:text-rose-400">{euro(co)}</TableCell>
                      <TableCell className={`text-right font-semibold ${ut >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid={`margine-utile-${i.numero}`}>{euro(ut)}</TableCell>
                    </TableRow>
                  );
                })}
            {invoices.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nessuna fattura.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!pdfDoc} onOpenChange={(o) => !o && setPdfDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0">
          <DialogTitle className="sr-only">Anteprima fattura N. {pdfDoc?.numero}</DialogTitle>
          <DialogDescription className="sr-only">Anteprima e download della fattura in PDF.</DialogDescription>
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-border bg-card no-print">
            <div className="font-heading font-semibold">Anteprima fattura N. {pdfDoc?.numero}</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Stampa</Button>
              <Button size="sm" onClick={() => downloadElementPdf("print-area", `Fattura-${pdfDoc?.numero}.pdf`)} data-testid="invoice-pdf-export-button"><Download className="w-4 h-4 mr-1.5" /> Scarica PDF</Button>
            </div>
          </div>
          <div className="p-4 bg-slate-100 dark:bg-slate-900">
            <QuotePDF quote={pdfDoc} settings={settings} contact={contactOf(pdfDoc)} tipo="FATTURA" />
          </div>
        </DialogContent>
      </Dialog>

      <SendDialog open={!!sendDoc} onClose={closeSend} doc={sendDoc} contact={contactOf(sendDoc)} settings={settings} docType="fattura" initialChannel={params.get("canale") === "email" ? "email" : "whatsapp"} />

      <Dialog open={!!dueDoc} onOpenChange={(o) => !o && setDueDoc(null)}>
        <DialogContent className="max-w-sm" data-testid="invoice-due-dialog" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveDue(); } }}>
          <DialogHeader>
            <DialogTitle className="font-heading">Scadenza pagamento — Fattura N. {dueDoc?.numero}</DialogTitle>
            <DialogDescription className="sr-only">Imposta la data di scadenza del pagamento della fattura.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Data di scadenza</Label>
            <DateInput value={dueVal} onChange={(e) => setDueVal(e.target.value)} data-testid="invoice-due-input" />
            <p className="text-xs text-muted-foreground">Riceverai un promemoria nella campanella quando la scadenza è entro 7 giorni.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDueDoc(null)}>Annulla</Button>
            <Button onClick={saveDue} data-testid="invoice-due-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rateDoc} onOpenChange={(o) => !o && setRateDoc(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-rate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Piano di pagamento — Fattura N. {rateDoc?.numero}</DialogTitle>
            <DialogDescription className="sr-only">Definisci le rate in percentuale, le scadenze e lo stato di pagamento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-muted-foreground">Totale fattura: <span className="font-semibold text-foreground">{euro(rateDoc?.totale || 0)}</span></div>
              <div className="flex gap-1.5 flex-wrap">
                {RATE_PRESETS.map((p) => (
                  <Button key={p.key} size="sm" variant="outline" onClick={() => applyPreset(p)} data-testid={`rate-preset-${p.key}`}>{p.label}</Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {rateRows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end rounded-lg border border-border p-3 bg-secondary/30" data-testid={`rate-row-${i}`}>
                  <div className="col-span-12 sm:col-span-4 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Descrizione</Label>
                    <Input value={r.etichetta} onChange={(e) => updateRow(i, { etichetta: e.target.value })} className="h-9" data-testid={`rate-etichetta-${i}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">%</Label>
                    <Input type="number" min="0" max="100" value={r.percentuale} onChange={(e) => updateRow(i, { percentuale: e.target.value })} className="h-9" data-testid={`rate-perc-${i}`} />
                  </div>
                  <div className="col-span-8 sm:col-span-3 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Scadenza</Label>
                    <DateInput value={r.scadenza} onChange={(e) => updateRow(i, { scadenza: e.target.value })} className="h-9" data-testid={`rate-scadenza-${i}`} />
                  </div>
                  <div className="col-span-8 sm:col-span-2 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Importo</Label>
                    <div className="h-9 flex items-center text-sm font-medium">{euro((rateDoc?.totale || 0) * (Number(r.percentuale) || 0) / 100)}</div>
                  </div>
                  <div className="col-span-4 sm:col-span-1 flex items-center justify-between sm:justify-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                      <input type="checkbox" checked={!!r.pagata} onChange={(e) => updateRow(i, { pagata: e.target.checked })} className="accent-primary w-4 h-4" data-testid={`rate-pagata-${i}`} />
                      Pag.
                    </label>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeRow(i)} data-testid={`rate-remove-${i}`}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={addRow} data-testid="rate-add-row"><Plus className="w-4 h-4 mr-1" /> Aggiungi rata</Button>
              <div className={`text-sm font-semibold ${Math.round(rateSum) === 100 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid="rate-sum">
                Totale percentuali: {rateSum}%
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDoc(null)}>Annulla</Button>
            <Button onClick={saveRate} disabled={savingRate} data-testid="rate-save">Salva piano</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!matDoc} onOpenChange={(o) => !o && setMatDoc(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="invoice-materiali-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Materiali e costi — Fattura N. {matDoc?.numero}</DialogTitle>
            <DialogDescription className="sr-only">Registra le spese di materiali e forniture per questo lavoro.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Registra le spese di materiali e forniture per questo lavoro. Verranno sottratte dall'utile netto in Panoramica.</p>
            <div className="space-y-2">
              {matRows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end rounded-lg border border-border p-3 bg-secondary/30" data-testid={`mat-row-${i}`}>
                  <div className="col-span-12 sm:col-span-5 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Descrizione</Label>
                    <Input value={r.descrizione} onChange={(e) => updateMatRow(i, { descrizione: e.target.value })} className="h-9" placeholder="Es. Piastrelle gres" data-testid={`mat-descrizione-${i}`} />
                  </div>
                  <div className="col-span-6 sm:col-span-3 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Fornitore</Label>
                    <Input value={r.fornitore} onChange={(e) => updateMatRow(i, { fornitore: e.target.value })} className="h-9" data-testid={`mat-fornitore-${i}`} />
                  </div>
                  <div className="col-span-6 sm:col-span-2 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Importo €</Label>
                    <Input type="number" min="0" step="0.01" value={r.importo} onChange={(e) => updateMatRow(i, { importo: e.target.value })} className="h-9" data-testid={`mat-importo-${i}`} />
                  </div>
                  <div className="col-span-10 sm:col-span-2 space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Data</Label>
                    <DateInput value={r.data} onChange={(e) => updateMatRow(i, { data: e.target.value })} className="h-9" data-testid={`mat-data-${i}`} />
                  </div>
                  <div className="col-span-2 sm:col-span-12 flex sm:justify-end">
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeMatRow(i)} data-testid={`mat-remove-${i}`}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
              {matRows.length === 0 && <div className="text-center py-6 text-sm text-muted-foreground">Nessun costo registrato.</div>}
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={addMatRow} data-testid="mat-add-row"><Plus className="w-4 h-4 mr-1" /> Aggiungi costo</Button>
                <input ref={matReceiptRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={uploadMatReceipt} className="hidden" data-testid="mat-receipt-input" />
                <Button variant="secondary" size="sm" onClick={() => matReceiptRef.current?.click()} disabled={matOcrLoading} data-testid="mat-receipt-upload">
                  {matOcrLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ScanLine className="w-4 h-4 mr-1" />} Carica scontrino/bolla
                </Button>
              </div>
              <div className="text-sm font-semibold" data-testid="mat-sum">Totale costi: {euro(matRowsSum)}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatDoc(null)}>Annulla</Button>
            <Button onClick={saveMat} disabled={savingMat} data-testid="mat-save">Salva costi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la fattura?</AlertDialogTitle>
            <AlertDialogDescription>La fattura N. {toDelete?.numero} sarà eliminata definitivamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
